import React, { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ZoomIn,
  ZoomOut,
  CenterFocusStrong,
  Refresh,
  Edit,
  EditOff,
} from '@mui/icons-material';
import { Graph, Edge, Node as X6Node, Cell, Selection, Keyboard } from '@antv/x6';
import { useSnackbar } from 'notistack';
import { STATUS_COLORS } from './monitoramentoRedeUtils';
import {
  buildDiagramGraph,
  buildEdgeEndpoint,
  isDiagramPortId,
  parseNodeId,
  type DiagramGraphData,
  type DiagramEdgeSpec,
} from './diagramaRedeUtils';
import { UseRedeDiagramaReturn } from './useRedeDiagrama';
import {
  ensureDiagramX6Registered,
  SHAPE_NAME,
  REDE_EDGE_SHAPE,
  buildRedeEdgeAttrs,
  applyEdgeVisualState,
  X6ReactPortalProvider,
} from './x6DiagramSetup';
import './diagramaEdgeEffects.css';
import { SavePosicaoPayload } from '../../../services/redeDiagramaService';
import { startRedeEdgeFlowLoop } from './diagramaEdgeFlow';

const PORT_GROUPS = {
  groups: {
    top: {
      position: 'top',
      attrs: {
        circle: { r: 5, magnet: true, stroke: '#5F95FF', strokeWidth: 1, fill: '#fff' },
      },
    },
    right: {
      position: 'right',
      attrs: {
        circle: { r: 5, magnet: true, stroke: '#5F95FF', strokeWidth: 1, fill: '#fff' },
      },
    },
    bottom: {
      position: 'bottom',
      attrs: {
        circle: { r: 5, magnet: true, stroke: '#5F95FF', strokeWidth: 1, fill: '#fff' },
      },
    },
    left: {
      position: 'left',
      attrs: {
        circle: { r: 5, magnet: true, stroke: '#5F95FF', strokeWidth: 1, fill: '#fff' },
      },
    },
  },
  items: [
    { id: 'top', group: 'top' },
    { id: 'right', group: 'right' },
    { id: 'bottom', group: 'bottom' },
    { id: 'left', group: 'left' },
  ],
};

const DEFAULT_EDGE_LINE = {
  stroke: '#2196f3',
  strokeWidth: 2,
} as const;

interface DiagramEdgeData {
  conexaoId?: number;
  baseStrokeWidth?: number;
  monitorStatus?: 'online' | 'offline' | 'unknown' | 'neutral';
}

function getTerminalCellPort(terminal: unknown): { cell?: string; port?: string } {
  if (terminal && typeof terminal === 'object' && 'cell' in terminal) {
    const data = terminal as { cell?: unknown; port?: unknown };
    return {
      cell: typeof data.cell === 'string' ? data.cell : undefined,
      port: typeof data.port === 'string' ? data.port : undefined,
    };
  }
  return {};
}

function applyEdgeEndpoints(edge: Edge, edgeSpec: DiagramEdgeSpec) {
  const { cell: sourceCell, port: sourcePort } = getTerminalCellPort(edge.getSource());
  const { cell: targetCell, port: targetPort } = getTerminalCellPort(edge.getTarget());

  if (sourceCell !== edgeSpec.source || sourcePort !== edgeSpec.sourcePort) {
    edge.setSource(buildEdgeEndpoint(edgeSpec.source, edgeSpec.sourcePort));
  }
  if (targetCell !== edgeSpec.target || targetPort !== edgeSpec.targetPort) {
    edge.setTarget(buildEdgeEndpoint(edgeSpec.target, edgeSpec.targetPort));
  }
}

function syncGraphWithData(graph: Graph, data: DiagramGraphData) {
  const desiredNodeIds = new Set(data.nodes.map((n) => n.id));
  const desiredEdgeIds = new Set(data.edges.map((e) => e.id));

  graph.batchUpdate('sync-diagram', () => {
    graph.getEdges().forEach((edge) => {
      const edgeData = edge.getData<DiagramEdgeData>();
      if (desiredEdgeIds.has(edge.id)) return;
      // Preserva aresta recém-desenhada aguardando persistência na API.
      if (!edgeData?.conexaoId) return;
      graph.removeEdge(edge.id);
    });

    graph.getNodes().forEach((node) => {
      if (!desiredNodeIds.has(node.id)) {
        graph.removeNode(node.id);
      }
    });

    data.nodes.forEach((nodeSpec) => {
      const existing = graph.getCellById(nodeSpec.id);
      if (existing?.isNode()) {
        const { width, height } = existing.size();
        const shapeChanged =
          existing.shape !== nodeSpec.shape ||
          width !== nodeSpec.width ||
          height !== nodeSpec.height;

        if (shapeChanged) {
          graph.removeNode(existing.id, { silent: true });
          graph.addNode({
            id: nodeSpec.id,
            shape: nodeSpec.shape,
            x: nodeSpec.x,
            y: nodeSpec.y,
            width: nodeSpec.width,
            height: nodeSpec.height,
            data: nodeSpec.data,
            ports: PORT_GROUPS,
          });
          return;
        }

        existing.setData(nodeSpec.data);
        if (!graph.isSelected(existing)) {
          const { x, y } = existing.position();
          if (x !== nodeSpec.x || y !== nodeSpec.y) {
            existing.setPosition(nodeSpec.x, nodeSpec.y, { silent: true });
          }
        }
        return;
      }

      graph.addNode({
        id: nodeSpec.id,
        shape: nodeSpec.shape,
        x: nodeSpec.x,
        y: nodeSpec.y,
        width: nodeSpec.width,
        height: nodeSpec.height,
        data: nodeSpec.data,
        ports: PORT_GROUPS,
      });
    });

    data.edges.forEach((edgeSpec) => {
      const existing = graph.getCellById(edgeSpec.id);
      if (existing?.isEdge()) {
        existing.setData({
          conexaoId: edgeSpec.conexaoId,
          baseStrokeWidth: edgeSpec.strokeWidth,
          monitorStatus: edgeSpec.status,
        });
        applyEdgeEndpoints(existing, edgeSpec);
        existing.attr('line/stroke', edgeSpec.stroke);
        if (!graph.isSelected(existing)) {
          existing.attr('line/strokeWidth', edgeSpec.strokeWidth);
        }
        applyEdgeVisualState(existing, edgeSpec.status);
        return;
      }

      const edge = graph.addEdge({
        id: edgeSpec.id,
        shape: REDE_EDGE_SHAPE,
        source: buildEdgeEndpoint(edgeSpec.source, edgeSpec.sourcePort),
        target: buildEdgeEndpoint(edgeSpec.target, edgeSpec.targetPort),
        data: {
          conexaoId: edgeSpec.conexaoId,
          baseStrokeWidth: edgeSpec.strokeWidth,
          monitorStatus: edgeSpec.status,
        },
        attrs: buildRedeEdgeAttrs(edgeSpec.stroke, edgeSpec.strokeWidth),
        router: { name: 'manhattan' },
        connector: { name: 'rounded' },
        zIndex: 0,
      });
      applyEdgeVisualState(edge, edgeSpec.status);
    });
  });
}

interface DiagramaTabProps {
  diagramaState: UseRedeDiagramaReturn;
}

const DiagramaTab: React.FC<DiagramaTabProps> = ({ diagramaState }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const containerRef = useRef<HTMLDivElement>(null);
  const graphRef = useRef<Graph | null>(null);
  const disposingRef = useRef(false);
  const isSyncingRef = useRef(false);
  const connectingRef = useRef(false);
  const savePosTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingPositionsRef = useRef<Map<string, SavePosicaoPayload>>(new Map());
  const persistedPortsRef = useRef<Set<number>>(new Set());
  const diagramaStateRef = useRef(diagramaState);
  const editModeRef = useRef(false);
  const selectionPluginRef = useRef<Selection | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [selectedEdgeId, setSelectedEdgeId] = useState<number | null>(null);

  editModeRef.current = editMode;

  const {
    nodes,
    conexoes,
    loading,
    loadDiagrama,
    removeConexao,
  } = diagramaState;

  diagramaStateRef.current = diagramaState;

  const diagramData = useMemo(
    () => buildDiagramGraph(nodes, conexoes),
    [nodes, conexoes]
  );

  const diagramSyncKey = useMemo(
    () => JSON.stringify({
      nodes: nodes.map((n) => {
        const latencia = n.dispositivo?.monitor_latencia_ms ?? '';
        return `${n.node_id}:${n.monitor_status}:${n.nome}:${latencia}`;
      }),
      conexoes: conexoes.map((c) => `${c.id}:${c.origem_port ?? ''}:${c.destino_port ?? ''}`),
      ready: !loading,
    }),
    [nodes, conexoes, loading]
  );

  const flushPendingPositions = useCallback(() => {
    const batch = Array.from(pendingPositionsRef.current.values());
    pendingPositionsRef.current.clear();
    if (batch.length === 0) return;

    diagramaStateRef.current.savePosicoes(batch).catch(() => {
      enqueueSnackbar('Erro ao salvar posição do nó', { variant: 'error' });
    });
  }, [enqueueSnackbar]);

  const scheduleSavePosition = useCallback((nodeId: string, x: number, y: number) => {
    const parsed = parseNodeId(nodeId);
    if (!parsed) return;

    pendingPositionsRef.current.set(nodeId, {
      entidade_tipo: parsed.tipo,
      entidade_id: parsed.id,
      pos_x: x,
      pos_y: y,
    });

    if (savePosTimerRef.current) {
      clearTimeout(savePosTimerRef.current);
    }

    savePosTimerRef.current = setTimeout(() => {
      savePosTimerRef.current = null;
      flushPendingPositions();
    }, 700);
  }, [flushPendingPositions]);

  useEffect(() => {
    ensureDiagramX6Registered();
    const container = containerRef.current;
    if (!container) return undefined;

    disposingRef.current = false;

    const graph = new Graph({
      container,
      autoResize: true,
      background: {
        color: alpha(theme.palette.background.default, 0.35),
      },
      grid: {
        size: 12,
        visible: true,
        type: 'dot',
        args: { color: alpha(theme.palette.divider, 0.45), thickness: 1 },
      },
      panning: { enabled: true },
      mousewheel: {
        enabled: true,
        modifiers: ['ctrl', 'meta'],
        minScale: 0.35,
        maxScale: 2.5,
      },
      connecting: {
        snap: { radius: 24 },
        allowBlank: false,
        allowLoop: false,
        allowNode: false,
        allowPort: true,
        highlight: true,
        connectionPoint: 'anchor',
        connector: { name: 'rounded' },
        router: { name: 'manhattan' },
        createEdge(): Edge {
          return graph.createEdge({
            shape: REDE_EDGE_SHAPE,
            attrs: buildRedeEdgeAttrs(DEFAULT_EDGE_LINE.stroke, DEFAULT_EDGE_LINE.strokeWidth),
            router: { name: 'manhattan' },
            connector: { name: 'rounded' },
            zIndex: 0,
          });
        },
        validateConnection({ sourceCell, targetCell, sourcePort, targetPort }) {
          if (!editModeRef.current) return false;
          if (!sourceCell || !targetCell || !sourcePort || !targetPort) return false;
          return sourceCell.id !== targetCell.id;
        },
      },
      interacting: () => ({
        nodeMovable: editModeRef.current,
        magnetConnectable: editModeRef.current,
        edgeMovable: false,
        vertexMovable: false,
        vertexAddable: false,
        vertexDeletable: false,
        edgeLabelMovable: false,
        arrowheadMovable: false,
      }),
    });

    graphRef.current = graph;

    const selectionPlugin = new Selection({
      enabled: true,
      rubberband: false,
      showNodeSelectionBox: false,
      showEdgeSelectionBox: false,
      selectCellOnMoved: true,
      pointerEvents: 'auto',
    });
    graph.use(selectionPlugin);
    selectionPlugin.disable();
    selectionPluginRef.current = selectionPlugin;

    graph.use(
      new Keyboard({
        enabled: true,
        global: true,
        guard: (_e) => {
          const target = _e.target as HTMLElement | null;
          if (!target) return true;
          const tag = target.tagName;
          if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) {
            return false;
          }
          return graph.getSelectedCellCount() > 0;
        },
      })
    );

    graph.on('edge:click', ({ edge }: { edge: Edge }) => {
      if (!editModeRef.current) return;
      graph.resetSelection(edge);
    });

    graph.on('edge:selected', ({ edge }: { edge: Edge }) => {
      const conexaoId = edge.getData<DiagramEdgeData>()?.conexaoId;
      setSelectedEdgeId(conexaoId ?? null);
      edge.attr('line/strokeWidth', 4);
    });

    graph.on('edge:unselected', ({ edge }: { edge: Edge }) => {
      const baseWidth = edge.getData<DiagramEdgeData>()?.baseStrokeWidth ?? 2;
      edge.attr('line/strokeWidth', baseWidth);
    });

    graph.on('selection:changed', ({ selected }: { selected: Cell[] }) => {
      const selectedEdge = selected.find((cell) => cell.isEdge()) as Edge | undefined;
      setSelectedEdgeId(selectedEdge?.getData<DiagramEdgeData>()?.conexaoId ?? null);
    });

    graph.on('blank:click', () => {
      setSelectedEdgeId(null);
    });

    graph.on('edge:connected', async ({ edge, isNew }: { edge: Edge; isNew?: boolean }) => {
      if (!isNew || disposingRef.current || !editModeRef.current) return;

      const sourceId = edge.getSourceCellId();
      const targetId = edge.getTargetCellId();
      const origemPort = edge.getSourcePortId();
      const destinoPort = edge.getTargetPortId();
      const origem = parseNodeId(sourceId);
      const destino = parseNodeId(targetId);

      if (!origem || !destino) {
        graph.removeEdge(edge.id);
        enqueueSnackbar('Conexão inválida entre os dispositivos selecionados', { variant: 'warning' });
        return;
      }

      if (!isDiagramPortId(origemPort) || !isDiagramPortId(destinoPort)) {
        graph.removeEdge(edge.id);
        enqueueSnackbar('Conecte pelos pontos azuis nas laterais do dispositivo', { variant: 'warning' });
        return;
      }

      connectingRef.current = true;
      try {
        const conexao = await diagramaStateRef.current.createConexao({
          origem_tipo: origem.tipo,
          origem_id: origem.id,
          destino_tipo: destino.tipo,
          destino_id: destino.id,
          origem_port: origemPort,
          destino_port: destinoPort,
        });

        if (disposingRef.current || graphRef.current !== graph) return;

        const targetNode = graph.getCellById(targetId);
        const targetStatus =
          targetNode?.isNode()
            ? (targetNode.getData<{ node?: { monitor_status?: DiagramEdgeData['monitorStatus'] } }>()?.node
                ?.monitor_status ?? 'unknown')
            : 'unknown';

        edge.setData({
          conexaoId: conexao.id,
          baseStrokeWidth: 2,
          monitorStatus: targetStatus,
        });
        applyEdgeVisualState(edge, targetStatus);
        if (edge.id !== `conexao-${conexao.id}`) {
          graph.updateCellId(edge, `conexao-${conexao.id}`);
        }
      } catch (err: unknown) {
        if (!disposingRef.current && graphRef.current === graph) {
          graph.removeEdge(edge.id);
        }
        const message =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
            : undefined;
        if (message) {
          enqueueSnackbar(message, { variant: 'error' });
        }
      } finally {
        connectingRef.current = false;
      }
    });

    graph.on('node:change:position', ({ node }: { node: X6Node }) => {
      if (
        disposingRef.current ||
        isSyncingRef.current ||
        connectingRef.current ||
        !editModeRef.current
      ) {
        return;
      }
      const { x, y } = node.position();
      scheduleSavePosition(node.id, x, y);
    });

    const handleDelete = async () => {
      if (!editModeRef.current) return;

      const selected = graph.getSelectedCells().filter((cell: Cell) => cell.isEdge()) as Edge[];
      if (selected.length === 0) return;

      for (const edge of selected) {
        const conexaoId = edge.getData<DiagramEdgeData>()?.conexaoId;
        if (!conexaoId) {
          graph.removeEdge(edge.id);
          continue;
        }
        try {
          await diagramaStateRef.current.removeConexao(conexaoId);
        } catch {
          enqueueSnackbar('Erro ao remover conexão', { variant: 'error' });
        }
      }

      setSelectedEdgeId(null);
    };

    graph.bindKey(['delete', 'backspace'], handleDelete);

    const stopEdgeFlow = startRedeEdgeFlowLoop(
      () => graphRef.current,
      () => disposingRef.current || isSyncingRef.current || connectingRef.current
    );

    return () => {
      stopEdgeFlow();
      disposingRef.current = true;

      if (savePosTimerRef.current) {
        clearTimeout(savePosTimerRef.current);
        savePosTimerRef.current = null;
      }

      flushPendingPositions();
      selectionPluginRef.current = null;
      graphRef.current = null;

      window.setTimeout(() => {
        try {
          graph.dispose();
        } catch {
          // Evita race condition entre React e x6-react-shape no unmount.
        }
      }, 0);
    };
  }, [scheduleSavePosition, flushPendingPositions, enqueueSnackbar]);

  useEffect(() => {
    const graph = graphRef.current;
    const selection = selectionPluginRef.current;
    if (!graph || !selection) return;

    if (editMode) {
      selection.enable();
      return;
    }

    graph.cleanSelection();
    setSelectedEdgeId(null);
    selection.disable();
    flushPendingPositions();
  }, [editMode, flushPendingPositions]);

  useEffect(() => {
    const graph = graphRef.current;
    if (!graph) return;

    graph.drawBackground({
      color: alpha(theme.palette.background.default, 0.35),
    });
  }, [theme.palette.background.default]);

  useEffect(() => {
    const graph = graphRef.current;
    if (!graph || loading || disposingRef.current || connectingRef.current) return undefined;

    const data = diagramData;
    const hadNodes = graph.getNodes().length > 0;
    const frameId = window.requestAnimationFrame(() => {
      if (disposingRef.current || connectingRef.current || graphRef.current !== graph) return;

      isSyncingRef.current = true;
      try {
        syncGraphWithData(graph, data);
      } finally {
        window.requestAnimationFrame(() => {
          isSyncingRef.current = false;
        });
      }

      if (!hadNodes && data.nodes.length > 0) {
        graph.zoomToFit({ padding: 48, maxScale: 1.1 });
      }
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [diagramSyncKey, loading]);

  useEffect(() => {
    if (loading) return;

    const pending = diagramData.edges.filter(
      (edge) => edge.needsPortPersist && !persistedPortsRef.current.has(edge.conexaoId)
    );
    if (pending.length === 0) return;

    pending.forEach((edge) => {
      persistedPortsRef.current.add(edge.conexaoId);
      void diagramaStateRef.current
        .updateConexaoPortas(edge.conexaoId, {
          origem_port: edge.sourcePort,
          destino_port: edge.targetPort,
        })
        .catch(() => {
          persistedPortsRef.current.delete(edge.conexaoId);
        });
    });
  }, [diagramData, loading]);

  const handleZoom = (factor: number) => {
    graphRef.current?.zoom(factor, { absolute: false });
  };

  const handleFit = () => {
    if (!graphRef.current || diagramData.nodes.length === 0) return;
    graphRef.current.zoomToFit({ padding: 48, maxScale: 1.1 });
  };

  const handleDeleteSelectedEdge = async () => {
    if (!selectedEdgeId) return;
    try {
      await removeConexao(selectedEdgeId);
      setSelectedEdgeId(null);
    } catch {
      enqueueSnackbar('Erro ao remover conexão', { variant: 'error' });
    }
  };

  return (
    <>
      <X6ReactPortalProvider />
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            px: 2,
            py: 1.2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.background.paper, 0.85),
          }}
        >
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>
              Diagrama de rede
            </Typography>
            </Box>

          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
            {!editMode && (
              <Chip
                size="small"
                label="Somente visualização"
                variant="outlined"
                sx={{ mr: 0.5, display: { xs: 'none', sm: 'flex' } }}
              />
            )}
            <Tooltip title={editMode ? 'Desabilitar edição' : 'Habilitar edição'}>
              <IconButton
                size="small"
                onClick={() => setEditMode((enabled) => !enabled)}
                color={editMode ? 'primary' : 'default'}
              >
                {editMode ? <EditOff fontSize="small" /> : <Edit fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Atualizar">
              <IconButton size="small" onClick={() => void loadDiagrama()}>
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Aumentar zoom">
              <IconButton size="small" onClick={() => handleZoom(0.15)}>
                <ZoomIn fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Diminuir zoom">
              <IconButton size="small" onClick={() => handleZoom(-0.15)}>
                <ZoomOut fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Centralizar">
              <IconButton size="small" onClick={handleFit}>
                <CenterFocusStrong fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, position: 'relative', minHeight: 420 }}>
          <Box
            ref={containerRef}
            tabIndex={0}
            className={editMode ? undefined : 'rede-diagram-view-mode'}
            sx={{ width: '100%', height: '100%', outline: 'none' }}
          />

          {loading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                bgcolor: alpha(theme.palette.background.default, 0.72),
                zIndex: 2,
              }}
            >
              <CircularProgress size={44} thickness={4} />
              <Typography color="text.secondary" fontWeight={500}>
                Carregando diagrama...
              </Typography>
            </Box>
          )}

          {!loading && diagramData.nodes.length === 0 && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 3,
                bgcolor: alpha(theme.palette.background.default, 0.72),
                zIndex: 2,
              }}
            >
              <Alert severity="info" sx={{ maxWidth: 520 }}>
                Nenhum dispositivo disponível. Cadastre dispositivos na aba Dispositivos ou cadastre OLTs/NAS no sistema.
              </Alert>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default DiagramaTab;
